// Shared fetch layer for lendcord-core — every request needs X-Tenant-Id (see
// tokenStorage.ts's sibling comment / auth feature for why), and authenticated
// endpoints additionally need Authorization: Bearer <token>.

import { clearAccessToken, getAccessToken } from "./tokenStorage";

const API_BASE = process.env.API_URL;
const TENANT_ID = process.env.TENANT_ID ?? "";

// Matches lendcord-core's standard error envelope (see its Postman
// collection's top-level description) — code is null for framework-level
// field validation errors and only populated for catalogued LEND-XXX errors.
interface ApiErrorBody {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  code: string | null;
  path: string;
}

export class ApiError extends Error {
  status: number;
  code: string | null;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.status = body.status;
    this.code = body.code;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  // Attaches Authorization: Bearer <token> from the locally stored access
  // token — only endpoints documented as requiring a Bearer JWT need this.
  auth?: boolean;
  // Opt out of the auto-logout-on-401 behavior below — for the rare
  // authenticated endpoint where 401 is a legitimate business-logic outcome
  // rather than a bad/expired token (currently just POST /auth/password/change's
  // LEND-072 "wrong old password").
  skipAuthExpiryOn401?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = false, skipAuthExpiryOn401 = false } = options;
  const headers: Record<string, string> = { "X-Tenant-Id": TENANT_ID };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    throw new Error(`Request to ${path} failed: ${(error as Error).message}`);
  }

  if (!response.ok) {
    // For most endpoints requiring a Bearer token, 401 always means the
    // token itself is the problem (missing/expired/revoked/wrong tenant —
    // see the Postman collection) — never a business-logic outcome. Clearing
    // it here and broadcasting lets SessionContext flip every mounted route
    // guard to logged-out immediately, instead of leaving the app to keep
    // firing authenticated requests with a token the backend has already
    // rejected. skipAuthExpiryOn401 opts specific calls out of this.
    if (response.status === 401 && auth && !skipAuthExpiryOn401) {
      clearAccessToken();
      window.dispatchEvent(new Event("lendcord:session-expired"));
    }

    const errorBody = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      errorBody ?? {
        timestamp: new Date().toISOString(),
        status: response.status,
        error: response.statusText,
        message: `Request failed with status ${response.status}`,
        code: null,
        path,
      }
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

export function getJson<T>(path: string, options: { auth?: boolean } = {}): Promise<T> {
  return request<T>(path, { method: "GET", auth: options.auth });
}

export function postJson<T>(
  path: string,
  body?: unknown,
  options: { auth?: boolean; skipAuthExpiryOn401?: boolean } = {}
): Promise<T> {
  return request<T>(path, { method: "POST", body, auth: options.auth, skipAuthExpiryOn401: options.skipAuthExpiryOn401 });
}

export function putJson<T>(path: string, body?: unknown, options: { auth?: boolean } = {}): Promise<T> {
  return request<T>(path, { method: "PUT", body, auth: options.auth });
}

export function deleteJson<T>(path: string, body?: unknown, options: { auth?: boolean } = {}): Promise<T> {
  return request<T>(path, { method: "DELETE", body, auth: options.auth });
}
